import React from 'react';
import { ColumnDef, Row, Table } from '@tanstack/react-table';
import { IndeterminateCheckbox } from '../IndeterminateCheckbox/IndeterminateCheckbox';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@innovationnorway/component-library';

type CreateSelectionColumnOptions = {
  disabledRowTitle?: string;
  enableMultiRowSelection: boolean;
};

export function createSelectionColumn<TData extends object>({
  disabledRowTitle,
  enableMultiRowSelection,
}: CreateSelectionColumnOptions): ColumnDef<TData, any> {
  return {
    id: '__selection',
    header: ({ table }) => {
      return <MarkAllRowsCheckbox enableMultiRowSelection={enableMultiRowSelection} table={table} />;
    },
    cell: ({ row }) => {
      return <MarkRowCheckbox row={row} disabledRowTitle={disabledRowTitle} />;
    },
    size: 50,
    maxSize: 50,
    enableSorting: false,
  };
}

export function createExpanderColumm<TData extends object>(): ColumnDef<TData, any> {
  return {
    id: 'expander',
    header: () => null,
    cell: ({ row }) => {
      return <ExpanderButton row={row} />;
    },
    size: 15,
    maxSize: 15,
    enableSorting: false,
  };
}

function MarkAllRowsCheckbox<TData extends object>({
  table,
  enableMultiRowSelection,
}: {
  table: Table<TData>;
  enableMultiRowSelection: boolean;
}) {
  const { t: tTableTitles } = useTranslation('tableTitles');

  if (!enableMultiRowSelection) return <></>;

  return (
    <>
      <IndeterminateCheckbox
        className="margin-bottom--0 padding-x--0"
        title={tTableTitles('markAllRows')}
        {...{
          checked: table.getIsAllRowsSelected(),
          indeterminate: table.getIsSomeRowsSelected(),
          onChange: table.getToggleAllRowsSelectedHandler(),
        }}
      />
      <span className="sr-only">{tTableTitles('markAllRows')}</span>
    </>
  );
}

function MarkRowCheckbox<TData extends object>({
  row,
  disabledRowTitle,
}: {
  row: Row<TData>;
  disabledRowTitle?: string;
}) {
  const { t: tTableTitles } = useTranslation('tableTitles');
  const canSelect = row.getCanSelect();

  return (
    <IndeterminateCheckbox
      className="margin-bottom--0 padding-x--0"
      title={
        canSelect === false
          ? (disabledRowTitle ?? tTableTitles('rowCannotBeMarked'))
          : tTableTitles('markRow', {
              index: row.index + 1,
            })
      }
      {...{
        checked: row.getIsSelected(),
        disabled: !canSelect,
        indeterminate: row.getIsSomeSelected(),
        onChange: row.getToggleSelectedHandler(),
      }}
    />
  );
}

function ExpanderButton<TData extends object>({ row }: { row: Row<TData> }) {
  return (
    <IconButton
      className="expander-button"
      onClick={() => {
        row.toggleExpanded();
      }}
      iconName={row.getIsExpanded() ? 'fa-light fa-chevronDown' : 'fa-light fa-chevron-right'}
    />
  );
}
